import React from "react";
import { Plus, Trash2 } from "lucide-react";
import StableInput from "./StableInput";
import { defaultConfigItem } from "../../constants";

const ConfigurationGrid = ({
  category,
  subcategory,
  title,
  examples = [],
  questionnaireData,
  setQuestionnaireData,
}) => {
  const items = questionnaireData[category][subcategory] || [];

  const handlePopulateExamples = () => {
    // Create sample items from examples
    const sampleItems = examples.map((example) => ({
      ...defaultConfigItem,
      legacyName: example,
      legacyCode: example.substring(0, 3).toUpperCase(),
      description: `${example} description`,
      newName: example,
      newCode: example.substring(0, 3).toUpperCase() + "-WD",
      newDescription: `${example} in Workday`,
    }));

    setQuestionnaireData((prev) => {
      const updatedData = { ...prev };
      if (!updatedData[category]) {
        updatedData[category] = {};
      }
      updatedData[category][subcategory] = sampleItems;
      return updatedData;
    });
  };

  // Add a new config item to a specific category
  const addConfigItem = () => {
    setQuestionnaireData((prev) => {
      const updatedData = { ...prev };
      if (!updatedData[category]) {
        updatedData[category] = {};
      }
      if (!updatedData[category][subcategory]) {
        updatedData[category][subcategory] = [];
      }
      updatedData[category][subcategory] = [
        ...updatedData[category][subcategory],
        { ...defaultConfigItem },
      ];
      return updatedData;
    });
  };

  // Update a config item
  const updateConfigItem = (index, field, value) => {
    setQuestionnaireData((prev) => {
      // Make sure the categories exist
      if (!prev[category]) prev[category] = {};
      if (!prev[category][subcategory]) prev[category][subcategory] = [];

      // Create a copy of the items array
      const items = [...prev[category][subcategory]];

      // Create an updated item
      const updatedItem = { ...items[index], [field]: value };

      // If changing action to "merge", copy legacy values to new fields
      if (field === "action" && value === "merge") {
        updatedItem.newName = updatedItem.legacyName;
        updatedItem.newCode = updatedItem.legacyCode;
        updatedItem.newDescription = updatedItem.description;
      }

      // Replace the item in the array
      items[index] = updatedItem;

      // Return the updated state
      return {
        ...prev,
        [category]: {
          ...prev[category],
          [subcategory]: items,
        },
      };
    });
  };

  // Remove a config item
  const removeConfigItem = (index) => {
    setQuestionnaireData((prev) => {
      // Make sure the categories exist
      if (!prev[category] || !prev[category][subcategory]) return prev;

      const updatedData = { ...prev };
      updatedData[category][subcategory] = updatedData[category][
        subcategory
      ].filter((_, i) => i !== index);
      return updatedData;
    });
  };

  return (
    <div className="mt-4 space-y-4">
      <div className="flex justify-between">
        <h3 className="text-lg font-medium text-gray-900">{title}</h3>
        <div className="space-x-2">
          {examples.length > 0 && (
            <button
              type="button"
              className="px-3 py-1 text-xs font-medium rounded bg-gray-100 text-gray-700 hover:bg-gray-200"
              onClick={handlePopulateExamples}
            >
              Populate Examples
            </button>
          )}
          <button
            type="button"
            className="px-3 py-1 text-xs font-medium rounded bg-blue-50 text-blue-700 hover:bg-blue-100"
            onClick={addConfigItem}
          >
            <Plus size={12} className="inline mr-1" />
            Add Item
          </button>
        </div>
      </div>

      {items.length === 0 ? (
        <div className="text-center p-4 border border-dashed border-gray-300 rounded-lg">
          <p className="text-gray-500 text-sm">
            No items added yet. Click "Add Item" to begin
            {examples.length > 0
              ? ' or "Populate Examples" to use sample data'
              : ""}
            .
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th
                  scope="col"
                  className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  Legacy Name
                </th>
                <th
                  scope="col"
                  className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  Legacy Code
                </th>
                <th
                  scope="col"
                  className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  Description
                </th>
                <th
                  scope="col"
                  className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  New Name
                </th>
                <th
                  scope="col"
                  className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  New Code
                </th>
                <th
                  scope="col"
                  className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  New Description
                </th>
                <th
                  scope="col"
                  className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  Comments
                </th>
                <th
                  scope="col"
                  className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  Action
                </th>
                <th
                  scope="col"
                  className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider"
                ></th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {items.map((item, index) => (
                <tr key={index} className="hover:bg-gray-50">
                  <td className="px-3 py-2">
                    <StableInput
                      initialValue={item.legacyName}
                      onSave={(value) =>
                        updateConfigItem(index, "legacyName", value)
                      }
                      className={
                        item.action === "notRequired"
                          ? "bg-gray-100 text-gray-400"
                          : ""
                      }
                      disabled={item.action === "notRequired"}
                    />
                  </td>
                  <td className="px-3 py-2">
                    <StableInput
                      initialValue={item.legacyCode}
                      onSave={(value) =>
                        updateConfigItem(index, "legacyCode", value)
                      }
                      className={
                        item.action === "notRequired"
                          ? "bg-gray-100 text-gray-400"
                          : ""
                      }
                      disabled={item.action === "notRequired"}
                    />
                  </td>
                  <td className="px-3 py-2">
                    <StableInput
                      initialValue={item.description}
                      onSave={(value) =>
                        updateConfigItem(index, "description", value)
                      }
                      className={
                        item.action === "notRequired"
                          ? "bg-gray-100 text-gray-400"
                          : ""
                      }
                      disabled={item.action === "notRequired"}
                    />
                  </td>
                  <td className="px-3 py-2">
                    <StableInput
                      initialValue={item.newName}
                      onSave={(value) =>
                        updateConfigItem(index, "newName", value)
                      }
                      className={
                        item.action === "notRequired"
                          ? "bg-gray-100 text-gray-400"
                          : ""
                      }
                      disabled={item.action === "notRequired"}
                    />
                  </td>
                  <td className="px-3 py-2">
                    <StableInput
                      initialValue={item.newCode}
                      onSave={(value) =>
                        updateConfigItem(index, "newCode", value)
                      }
                      className={
                        item.action === "notRequired"
                          ? "bg-gray-100 text-gray-400"
                          : ""
                      }
                      disabled={item.action === "notRequired"}
                    />
                  </td>
                  <td className="px-3 py-2">
                    <StableInput
                      initialValue={item.newDescription}
                      onSave={(value) =>
                        updateConfigItem(index, "newDescription", value)
                      }
                      className={
                        item.action === "notRequired"
                          ? "bg-gray-100 text-gray-400"
                          : ""
                      }
                      disabled={item.action === "notRequired"}
                    />
                  </td>
                  <td className="px-3 py-2">
                    <StableInput
                      initialValue={item.comments}
                      onSave={(value) =>
                        updateConfigItem(index, "comments", value)
                      }
                      className={
                        item.action === "notRequired"
                          ? "bg-gray-100 text-gray-400"
                          : ""
                      }
                      disabled={item.action === "notRequired"}
                    />
                  </td>
                  <td className="px-3 py-2">
                    <select
                      className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                      value={item.action || "keep"}
                      onChange={(e) =>
                        updateConfigItem(index, "action", e.target.value)
                      }
                    >
                      <option value="keep">Keep</option>
                      <option value="merge">Merge</option>
                      <option value="notRequired">Not Required</option>
                    </select>
                  </td>
                  <td className="px-3 py-2 text-center">
                    <button
                      type="button"
                      className="text-red-600 hover:text-red-900"
                      onClick={() => removeConfigItem(index)}
                    >
                      <Trash2 size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ConfigurationGrid;
